<template>
  <div class="users">
    <div class="page-header">
      <h1 class="page-title">{{ $t('customerUsersList.title') }}</h1>
      <button class="add-user-btn" @click="showAddUserModal = true">
        <i class="fas fa-plus"></i>
        {{ $t('customerUsersList.addUser') }}
      </button>
    </div>

    <!-- View Toggle -->
    <div class="view-toggle">
      <button 
        :class="['view-btn', { active: viewMode === 'list' }]" 
        @click="viewMode = 'list'"
      >
        <i class="fas fa-list"></i> {{ $t('customerUsersList.viewModes.list') }}
      </button>
      <button 
        :class="['view-btn', { active: viewMode === 'grid' }]" 
        @click="viewMode = 'grid'"
      >
        <i class="fas fa-th-large"></i> {{ $t('customerUsersList.viewModes.grid') }}
      </button>
    </div>

    <!-- Users List -->
    <div class="users-section">
      <div class="section-header">
        <h2>{{ $t('customerUsersList.teamMembers') }}</h2>
        <div class="search-bar">
          <i class="fas fa-search"></i>
          <input 
            type="text" 
            v-model="searchQuery" 
            :placeholder="$t('customerUsersList.search.placeholder')"
            class="search-input"
          />
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i>
        <p>{{ $t('customerUsersList.loading') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-state">
        <i class="fas fa-exclamation-circle"></i>
        <p>{{ error }}</p>
        <button @click="loadUsers" class="retry-btn">{{ $t('customerUsersList.error.retry') }}</button>
      </div>

      <!-- Empty State -->
      <div v-else-if="!users.length" class="empty-state">
        <i class="fas fa-users"></i>
        <p>{{ $t('customerUsersList.empty.title') }}</p>
        <button @click="showAddUserModal = true" class="add-btn">{{ $t('customerUsersList.empty.addUser') }}</button>
      </div>

      <!-- List View -->
      <div v-else-if="viewMode === 'list'" class="users-list">
        <div class="user-card" v-for="user in filteredUsers" :key="user.id">
          <div class="user-info">
            <div class="user-avatar" :style="{ backgroundColor: getAvatarColor(user.customer.firstName + ' ' + user.customer.lastName) }">
              {{ getInitials(user.customer.firstName + ' ' + user.customer.lastName) }}
            </div>
            <div class="user-details">
              <h3>{{ user.customer.firstName }} {{ user.customer.lastName }}</h3>
              <p>{{ user.customer.email }}</p>
              <span class="role-badge" :class="user.role.toLowerCase()">{{ $t(`customerUsersList.roles.${user.role === 'BusinessOwner' ? 'businessOwner' : user.role.toLowerCase()}`) }}</span>
            </div>
          </div>
          <div class="user-status" :class="{ active: user.customer.status === 'Active' }">
            {{ $t(`customerUsersList.status.${user.customer.status.toLowerCase()}`) }}
          </div>
          <div class="user-actions">
            <button class="action-btn" @click="editUser(user)">
              <i class="fas fa-edit"></i>
              {{ $t('customerUsersList.actions.edit') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Grid View -->
      <div v-else class="users-grid">
        <div class="user-card-grid" v-for="user in filteredUsers" :key="user.id">
          <div class="user-avatar-grid" :style="{ backgroundColor: getAvatarColor(user.customer.firstName + ' ' + user.customer.lastName) }">
            {{ getInitials(user.customer.firstName + ' ' + user.customer.lastName) }}
          </div>
          <div class="user-details-grid">
            <h3>{{ user.customer.firstName }} {{ user.customer.lastName }}</h3>
            <p>{{ user.customer.email }}</p>
            <span class="role-badge" :class="user.role.toLowerCase()">{{ $t(`customerUsersList.roles.${user.role === 'BusinessOwner' ? 'businessOwner' : user.role.toLowerCase()}`) }}</span>
            <div class="user-status" :class="{ active: user.customer.status === 'Active' }">
              {{ $t(`customerUsersList.status.${user.customer.status.toLowerCase()}`) }}
            </div>
          </div>
          <div class="user-actions-grid">
            <button class="action-btn" @click="editUser(user)">
              <i class="fas fa-edit"></i>
              {{ $t('customerUsersList.actions.edit') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Roles & Permissions -->
    <div class="roles-section">
      <h2>{{ $t('customerUsersList.rolesAndPermissions.title') }}</h2>
      <div class="roles-grid">
        <div class="role-card" v-for="role in roles" :key="role.name">
          <div class="role-header">
            <h3>{{ $t(`customerUsersList.roles.${role.name.toLowerCase()}`) }}</h3>
            <p>{{ role.description }}</p>
          </div>
          <div class="permissions-list">
            <div class="permission-item" v-for="permission in role.permissions" :key="permission">
              <i class="fas fa-check"></i>
              <span>{{ permission }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add User Modal -->
    <div class="modal" v-if="showAddUserModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ $t('customerUsersList.modals.addUser.title') }}</h2>
          <button class="close-btn" @click="showAddUserModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="addUser" class="add-user-form">
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.addUser.form.firstName') }}</label>
            <input type="text" v-model="newUser.firstName" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.addUser.form.lastName') }}</label>
            <input type="text" v-model="newUser.lastName" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.addUser.form.email') }}</label>
            <input type="email" v-model="newUser.email" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.addUser.form.phone') }}</label>
            <input type="tel" v-model="newUser.phone" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.addUser.form.role') }}</label>
            <select v-model="newUser.role" required>
              <option value="Employee">{{ $t('customerUsersList.roles.employee') }}</option>
              <option value="BusinessOwner">{{ $t('customerUsersList.roles.businessOwner') }}</option>
            </select>
          </div>
          <div class="modal-actions">
            <button type="button" class="cancel-btn" @click="showAddUserModal = false">{{ $t('customerUsersList.modals.addUser.buttons.cancel') }}</button>
            <button type="submit" class="submit-btn">{{ $t('customerUsersList.modals.addUser.buttons.submit') }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Edit User Modal -->
    <div class="modal" v-if="showEditUserModal">
      <div class="modal-content">
        <div class="modal-header">
          <h2>{{ $t('customerUsersList.modals.editUser.title') }}</h2>
          <button class="close-btn" @click="showEditUserModal = false">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <form @submit.prevent="updateUser" class="edit-user-form">
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.editUser.form.firstName') }}</label>
            <input type="text" v-model="editingUser.firstName" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.editUser.form.lastName') }}</label>
            <input type="text" v-model="editingUser.lastName" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.editUser.form.email') }}</label>
            <input type="email" v-model="editingUser.email" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.editUser.form.phone') }}</label>
            <input type="tel" v-model="editingUser.phone" required>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.editUser.form.role') }}</label>
            <select v-model="editingUser.role" required>
              <option value="Employee">{{ $t('customerUsersList.roles.employee') }}</option>
              <option value="BusinessOwner">{{ $t('customerUsersList.roles.businessOwner') }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>{{ $t('customerUsersList.modals.editUser.form.status') }}</label>
            <select v-model="editingUser.status" required>
              <option value="Active">{{ $t('customerUsersList.status.active') }}</option>
              <option value="Inactive">{{ $t('customerUsersList.status.inactive') }}</option>
            </select>
          </div>
          <div class="modal-actions">
            <button type="button" class="cancel-btn" @click="showEditUserModal = false">{{ $t('customerUsersList.modals.editUser.buttons.cancel') }}</button>
            <button type="submit" class="submit-btn">{{ $t('customerUsersList.modals.editUser.buttons.submit') }}</button>
          </div>
        </form>
      </div>
    </div>

    <!-- Toast Notifications -->
    <div class="toast-container">
      <div 
        v-for="(toast, index) in toasts" 
        :key="index" 
        :class="['toast', toast.type]"
        @click="removeToast(index)"
      >
        <i class="fas" :class="toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'"></i>
        <span>{{ toast.message }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import '@/styles/CustomerUsersList.css'
import businessService from '@/services/businessService'

export default {
  name: 'CustomerUsersList',
  props: {
    selectedBusiness: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      users: [],
      loading: false,
      error: null,
      searchQuery: '',
      showAddUserModal: false,
      showEditUserModal: false,
      viewMode: 'list', // 'list' or 'grid'
      toasts: [], // For toast notifications
      newUser: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Employee',
        status: 'Active'
      },
      editingUser: {
        id: null,
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: '',
        status: '',
        originalData: null
      },      roles: [
      ]
    }
  },
  watch: {
    selectedBusiness: {
      immediate: true,
      async handler(newBusiness) {
        if (newBusiness) {
          await this.loadUsers();
        }
      }
    }
  },
  computed: {
    filteredUsers() {
      if (!this.searchQuery) return this.users;
      const query = this.searchQuery.toLowerCase();
      return this.users.filter(user => 
        `${user.customer.firstName} ${user.customer.lastName}`.toLowerCase().includes(query) ||
        user.customer.email.toLowerCase().includes(query) ||
        user.role.toLowerCase().includes(query)
      );
    }
  },
  methods: {
    async loadUsers() {
      this.loading = true;
      this.error = null;
      try {
        // Check authentication state
        const customerToken = localStorage.getItem('customerToken');
        console.log('Customer token exists:', !!customerToken);
        
        if (!customerToken) {
          throw new Error('Not authenticated. Please log in.');
        }

        if (!this.selectedBusiness || !this.selectedBusiness.id) {
          console.error('No business selected or missing business ID');
          throw new Error('No business selected');
        }

        console.log('Loading users for business:', this.selectedBusiness.id);
        const response = await businessService.getBusinessUsers(this.selectedBusiness.id);
        console.log('Raw API response:', response);
        
        if (!response) {
          throw new Error('No response received from server');
        }

        if (response.success && Array.isArray(response.data)) {
          console.log('Processing successful response with data array');
          this.users = response.data;
          console.log('Users loaded:', this.users);
        } else if (Array.isArray(response)) {
          console.log('Processing direct array response');
          this.users = response;
          console.log('Users loaded (direct array):', this.users);
        } else if (response.data && Array.isArray(response.data)) {
          console.log('Processing response with data array');
          this.users = response.data;
          console.log('Users loaded (from data):', this.users);
        } else {
          console.error('Invalid response format:', response);
          throw new Error('Invalid response format from server');
        }

        // Log the final state
        console.log('Final users state:', {
          usersCount: this.users.length,
          users: this.users
        });
      } catch (error) {
        console.error('Error loading users:', error);
        this.error = error.message || 'Failed to load users. Please try again.';
        this.users = [];
      } finally {
        this.loading = false;
      }
    },    async handleAddUser(userData) {
      try {
        if (!this.selectedBusiness || !this.selectedBusiness.id) {
          throw new Error('No business selected');
        }

        // Convert email to lowercase before submission
        const processedUserData = {
          ...userData,
          email: userData.email.toLowerCase()
        };

        console.log('Adding user with data:', processedUserData);
        const response = await businessService.addUserToBusiness(this.selectedBusiness.id, processedUserData);
        
        if (response && response.data) {
          await this.loadUsers(); // Reload the users list
          this.showAddUserModal = false;
          this.resetNewUser();
          return response.data;
        } else {
          throw new Error('Invalid response format');
        }
      } catch (error) {
        console.error('Error adding user:', error);
        this.error = error.message || 'Failed to add user. Please try again.';
        throw error; // Re-throw for the caller to handle
      }
    },
    async handleRemoveUser(userId) {
      try {
        if (!this.selectedBusiness || !this.selectedBusiness.id) {
          throw new Error('No business selected');
        }

        await businessService.removeUserFromBusiness(this.selectedBusiness.id, userId);
        await this.loadUsers(); // Reload the users list
      } catch (error) {
        console.error('Error removing user:', error);
        this.error = error.message || 'Failed to remove user. Please try again.';
        throw error;
      }
    },
    getInitials(name) {
      return name.split(' ').map(n => n[0]).join('').toUpperCase();
    },
    getAvatarColor(name) {
      const colors = ['#006400', '#0369a1', '#7e22ce', '#dc2626', '#d97706'];
      const index = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return colors[index % colors.length];
    },    editUser(user) {
      // Clone the user information to avoid directly modifying the original
      this.editingUser = {
        id: user.id,
        firstName: user.customer.firstName,
        lastName: user.customer.lastName,
        email: user.customer.email,
        phone: user.customer.phone,
        role: user.role,
        status: user.customer.status,
        originalData: user // Keep reference to original for updating
      };
      this.showEditUserModal = true;
    },
      async updateUser() {
      try {
        if (!this.selectedBusiness || !this.selectedBusiness.id) {
          throw new Error('No business selected');
        }
        
        console.log('Updating user with data:', this.editingUser);
        
        // Convert email to lowercase before submission
        const processedUserData = {
          firstName: this.editingUser.firstName,
          lastName: this.editingUser.lastName,
          email: this.editingUser.email.toLowerCase(),
          phone: this.editingUser.phone,
          role: this.editingUser.role,
          status: this.editingUser.status
        };
        
        console.log('Sending normalized user data:', processedUserData);
        await businessService.updateUserDetails(this.editingUser.id, processedUserData);
        
        const user = this.editingUser.originalData;
        user.customer.firstName = this.editingUser.firstName;
        user.customer.lastName = this.editingUser.lastName;
        user.customer.email = this.editingUser.email.toLowerCase(); // Store normalized email
        user.customer.phone = this.editingUser.phone;
        user.role = this.editingUser.role;
        user.customer.status = this.editingUser.status;
        
        this.showEditUserModal = false;
        this.showToast(this.$t('customerUsersList.notifications.userUpdated'), 'success');
      } catch (error) {
        console.error('Error updating user:', error);
        this.error = error.message || this.$t('customerUsersList.notifications.error');
        this.showToast(this.error, 'error');
      }
    },    async addUser() {
      try {
        await this.handleAddUser(this.newUser);
        this.showToast(this.$t('customerUsersList.notifications.userAdded'), 'success');
      } catch (error) {
        console.error('Error in addUser:', error);
        this.showToast(this.$t('customerUsersList.notifications.error'), 'error');
      }
    },resetNewUser() {
      this.newUser = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        role: 'Employee',
        status: 'Active'
      };
    },
    
    // Toast notification methods
    showToast(message, type = 'success') {
      const toast = {
        id: Date.now(),
        message,
        type
      };
      this.toasts.push(toast);
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        this.removeToast(this.toasts.findIndex(t => t.id === toast.id));
      }, 5000);
    },
    
    removeToast(index) {
      if (index !== -1) {
        this.toasts.splice(index, 1);
      }
    }
  }
}
</script>

// Style block removed. All styles will be moved to CustomerUsersList.css.